'use client';

import { c as _c } from "react/compiler-runtime";
import { domMax, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { UserIdentity } from '@/components/profile/user-identity';
import { ReactionVoteButtons } from '@/components/shared/reaction-vote-buttons';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Tick01IconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { logger } from '@/lib/telemetry/logger';
import { MOTION_POSITION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import type { SuggestionWire } from './types';
import { effectiveImageUrl, effectiveLabel, effectiveTicker } from './types';
import { useSuggestionVoteModel } from './use-suggestion-vote-model';
interface SuggestionFeedRowProps {
  suggestion: SuggestionWire;
  isCreator?: boolean;
  isPromoted: boolean;
  reservedOutcomeIndex?: number;
  showSeparator?: boolean;
  onAddToMarket?: (() => void) | undefined;
}
export function SuggestionFeedRow(t0) {
  const $ = _c(82);
  const {
    suggestion,
    isCreator: t1,
    isPromoted,
    reservedOutcomeIndex,
    showSeparator: t2,
    onAddToMarket
  } = t0;
  const isCreator = t1 === undefined ? false : t1;
  const showSeparator = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== suggestion) {
    t3 = effectiveLabel(suggestion);
    $[0] = suggestion;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const label = t3;
  let t4;
  if ($[2] !== suggestion) {
    t4 = effectiveTicker(suggestion);
    $[2] = suggestion;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const ticker = t4;
  let t5;
  if ($[4] !== suggestion) {
    t5 = effectiveImageUrl(suggestion);
    $[4] = suggestion;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const imageUrl = t5;
  let t6;
  if ($[6] !== suggestion.suggesterPubkey) {
    t6 = suggestion.suggesterPubkey.slice(0, 4);
    $[6] = suggestion.suggesterPubkey;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== suggestion.suggesterPubkey) {
    t7 = suggestion.suggesterPubkey.slice(-4);
    $[8] = suggestion.suggesterPubkey;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const truncatedPubkey = `${t6}…${t7}`;
  let t8;
  if ($[10] !== suggestion.suggester.connectedProviders) {
    t8 = suggestion.suggester.connectedProviders ?? [];
    $[10] = suggestion.suggester.connectedProviders;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const connectedProviders = t8;
  const hasReason = suggestion.reason !== null && suggestion.reason.length > 0;
  const prefersReducedMotion = useReducedMotion();
  const outcomeIndex = suggestion.status === "promoting" ? reservedOutcomeIndex ?? null : suggestion.outcomeIndex;
  let t9;
  if ($[12] !== outcomeIndex) {
    t9 = Number.isInteger(outcomeIndex) && outcomeIndex !== null && outcomeIndex >= 0 && outcomeIndex < 10;
    $[12] = outcomeIndex;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const hasValidOutcomeIndex = t9;
  let t10;
  if ($[14] !== hasValidOutcomeIndex || $[15] !== label || $[16] !== outcomeIndex) {
    t10 = hasValidOutcomeIndex ? [{
      title: label,
      index: outcomeIndex
    }] : [];
    $[14] = hasValidOutcomeIndex;
    $[15] = label;
    $[16] = outcomeIndex;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== suggestion.marketPubkey) {
    t11 = {
      marketPubkey: suggestion.marketPubkey
    };
    $[18] = suggestion.marketPubkey;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const identityColors = useOutcomeColor(t10, t11);
  const identityColor = identityColors[0] ?? null;
  let t12;
  let t13;
  if ($[20] !== hasValidOutcomeIndex || $[21] !== suggestion.id || $[22] !== suggestion.status) {
    t12 = () => {
      if (suggestion.status !== "promoted" || hasValidOutcomeIndex) {
        return;
      }
      logger.warn("suggested_outcomes.promoted_index_missing", {
        suggestionId: suggestion.id,
        status: suggestion.status
      });
    };
    t13 = [hasValidOutcomeIndex, suggestion.id, suggestion.status];
    $[20] = hasValidOutcomeIndex;
    $[21] = suggestion.id;
    $[22] = suggestion.status;
    $[23] = t12;
    $[24] = t13;
  } else {
    t12 = $[23];
    t13 = $[24];
  }
  useEffect(t12, t13);
  const t14 = `suggestion-row-${suggestion.id}`;
  let t15;
  if ($[25] !== prefersReducedMotion) {
    t15 = prefersReducedMotion === true ? {
      duration: 0
    } : MOTION_POSITION;
    $[25] = prefersReducedMotion;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = isPromoted ? "true" : "false";
  const t17 = !isPromoted && showSeparator && "border-t border-border-secondary";
  const t18 = isPromoted && "rounded-panel-row border px-3";
  const t19 = isPromoted && identityColor === null && "border-border-secondary bg-surface-secondary";
  const t20 = isPromoted && identityColor?.outline;
  const t21 = isPromoted && identityColor !== null && `border-outcome-${identityColor.hue}-fill`;
  let t22;
  if ($[27] !== t17 || $[28] !== t18 || $[29] !== t19 || $[30] !== t20 || $[31] !== t21) {
    t22 = cn("grid grid-cols-[36px_1fr_auto] items-start gap-3 py-3", t17, t18, t19, t20, t21);
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== imageUrl || $[34] !== label) {
    t23 = <OptimizedImage variant="thumbnail" src={imageUrl} alt={label} size="size-9" sizes="36px" rounded="rounded-item" className="block size-9 shrink-0 self-start" imageClassName="size-9 object-cover" />;
    $[33] = imageUrl;
    $[34] = label;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[36] !== label) {
    t24 = <span className="truncate text-body font-medium text-foreground-primary">{label}</span>;
    $[36] = label;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== ticker) {
    t25 = <span className="shrink-0 font-mono text-caption text-foreground-secondary">${ticker}</span>;
    $[38] = ticker;
    $[39] = t25;
  } else {
    t25 = $[39];
  }
  let t26;
  if ($[40] !== t24 || $[41] !== t25) {
    t26 = <div className="flex items-baseline gap-2">{t24}{t25}</div>;
    $[40] = t24;
    $[41] = t25;
    $[42] = t26;
  } else {
    t26 = $[42];
  }
  let t27;
  if ($[43] !== connectedProviders || $[44] !== suggestion.suggester) {
    t27 = {
      ...suggestion.suggester,
      connectedProviders
    };
    $[43] = connectedProviders;
    $[44] = suggestion.suggester;
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  let t28;
  if ($[46] !== t27 || $[47] !== truncatedPubkey) {
    t28 = <UserIdentity user={t27} variant="inline" size="sm" fallback={truncatedPubkey} />;
    $[46] = t27;
    $[47] = truncatedPubkey;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <span aria-hidden="true" className="shrink-0">·</span>;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== suggestion.createdAt) {
    t30 = relativeTime(suggestion.createdAt);
    $[50] = suggestion.createdAt;
    $[51] = t30;
  } else {
    t30 = $[51];
  }
  let t31;
  if ($[52] !== t30) {
    t31 = <span className="shrink-0">{t30}</span>;
    $[52] = t30;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  let t32;
  if ($[54] !== t28 || $[55] !== t31) {
    t32 = <div className="flex min-w-0 items-center gap-1.5 text-caption text-foreground-tertiary">{t28}{t29}{t31}</div>;
    $[54] = t28;
    $[55] = t31;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  let t33;
  if ($[57] !== hasReason || $[58] !== suggestion.reason) {
    t33 = hasReason ? <p className="line-clamp-2 break-words text-caption text-foreground-secondary">{suggestion.reason}</p> : null;
    $[57] = hasReason;
    $[58] = suggestion.reason;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== t26 || $[61] !== t32 || $[62] !== t33) {
    t34 = <div className="flex min-w-0 flex-col gap-1 self-start">{t26}{t32}{t33}</div>;
    $[60] = t26;
    $[61] = t32;
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  const t35 = isPromoted ? "self-center" : "self-start";
  let t36;
  if ($[64] !== t35) {
    t36 = cn("flex items-center gap-2", t35);
    $[64] = t35;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] !== isCreator || $[67] !== isPromoted || $[68] !== onAddToMarket || $[69] !== suggestion) {
    t37 = isPromoted ? <CreatorStatus status={suggestion.status} /> : <ActiveRowActions suggestion={suggestion} isCreator={isCreator} onAddToMarket={onAddToMarket} />;
    $[66] = isCreator;
    $[67] = isPromoted;
    $[68] = onAddToMarket;
    $[69] = suggestion;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== t36 || $[72] !== t37) {
    t38 = <div className={t36}>{t37}</div>;
    $[71] = t36;
    $[72] = t37;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] !== t14 || $[75] !== t15 || $[76] !== t16 || $[77] !== t22 || $[78] !== t23 || $[79] !== t34 || $[80] !== t38) {
    t39 = <LazyMotion features={domMax}><m.div layout={true} layoutId={t14} transition={t15} data-promoted={t16} className={t22}>{t23}{t34}{t38}</m.div></LazyMotion>;
    $[74] = t14;
    $[75] = t15;
    $[76] = t16;
    $[77] = t22;
    $[78] = t23;
    $[79] = t34;
    $[80] = t38;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  return t39;
}
function CreatorStatus(t0) {
  const $ = _c(8);
  const {
    status
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes");
  let t1;
  if ($[0] !== status) {
    t1 = status === "promoted" ? <Icon icon={Tick01IconSolid} className="size-3.5" /> : null;
    $[0] = status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== status || $[3] !== t) {
    t2 = status === "promoted" ? t("addedByCreator") : t("creator.adding");
    $[2] = status;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <span className="inline-flex items-center gap-1.5 rounded-pill border border-feedback-success-border bg-feedback-success-surface px-2.5 py-1 text-caption font-medium text-feedback-success-content">{t1}{t2}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ActiveRowActions(t0) {
  const $ = _c(15);
  const {
    suggestion,
    isCreator,
    onAddToMarket
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      disabled: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const voteModel = useSuggestionVoteModel(suggestion.id, suggestion, t1);
  let t2;
  if ($[1] !== voteModel.downAriaLabel || $[2] !== voteModel.downCount || $[3] !== voteModel.onReact || $[4] !== voteModel.reaction || $[5] !== voteModel.upAriaLabel || $[6] !== voteModel.upCount) {
    t2 = <ReactionVoteButtons reaction={voteModel.reaction} upCount={voteModel.upCount} downCount={voteModel.downCount} upAriaLabel={voteModel.upAriaLabel} downAriaLabel={voteModel.downAriaLabel} onReact={voteModel.onReact} size="sm" />;
    $[1] = voteModel.downAriaLabel;
    $[2] = voteModel.downCount;
    $[3] = voteModel.onReact;
    $[4] = voteModel.reaction;
    $[5] = voteModel.upAriaLabel;
    $[6] = voteModel.upCount;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isCreator || $[9] !== onAddToMarket || $[10] !== t) {
    t3 = isCreator && onAddToMarket !== undefined ? <AnimatedButton type="button" variant="primary" size="sm" onClick={onAddToMarket}>{t("addToMarket")}</AnimatedButton> : null;
    $[8] = isCreator;
    $[9] = onAddToMarket;
    $[10] = t;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== t2 || $[13] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
}
function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const deltaSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}